// Print the exact Trust-approved uniform assignment that patients see online.
// Only trusted catalogue colour tokens and published, schema-validated custom
// SVG definitions are accepted; there are no uploaded or arbitrary image URLs.
import { resolvePublishedRoleUniform } from '../../../src/lib/publishedUniformModel.js';
import { resolveEasyReadPrintUniform } from './easyReadPrintUniforms.js';
import trustLibrary from '../../../src/data/trustRoleLibrary.generated.json' with { type: 'json' };

const colourById = new Map((trustLibrary.colours || [])
  .filter(colour => colour?.colour_id)
  .map(colour => [colour.colour_id, {
    body: colour.body_hex || '#CBD5E1',
    trim: colour.trim_hex || colour.body_hex || '#64748B',
  }]));
const roleKey = role => typeof role === 'string' ? role : role?.role_key || role?.roleKey || role?.id;

export function resolveTrustJourneyPrintUniform(role, trustConfig = {}, genericOverrides = {}) {
  const key = roleKey(role);
  const generic = () => resolveEasyReadPrintUniform({ id: key, roleKey: key, role_key: key }, genericOverrides);
  // Fixed national paramedic illustration cannot be changed by a Trust.
  if (key === 'paramedic') return generic();
  const roleConfig = trustConfig?.roles?.[key];
  const assigned = roleConfig?.uniform;
  if (!assigned) return generic();

  const custom = resolvePublishedRoleUniform(trustConfig, assigned);
  if (custom) {
    return {
      kind: 'garment', source: 'trust_custom', uniformDefinitionId: custom.id,
      colourId: custom.id, templateId: custom.templateId,
      body: custom.body_hex, trim: custom.trim_hex,
      pocketPipingHex: custom.pocketPipingHex,
      showBadge: custom.badge?.visible !== false,
      uniformName: custom.label,
    };
  }
  // Invalid or stale custom references fail closed to the supported generic
  // assignment, rather than silently representing a different Trust uniform.
  if (assigned.uniformDefinitionId) return { kind: 'unknown', colourId: null, source: 'invalid_trust_uniform' };

  const colourId = assigned.colourId || assigned.colorId || assigned.uniformId;
  const garmentId = assigned.garmentId || (colourId === 'no_uniform' ? 'no_uniform' : 'scrubs');
  if (!colourId) return generic();
  if (colourId === 'no_uniform' || garmentId === 'no_uniform') {
    return { kind: 'professional', colourId: 'no_uniform', source: 'trust_legacy' };
  }
  const colour = colourById.get(colourId);
  if (!colour) return { kind: 'unknown', colourId, source: 'unknown_trust_colour' };
  return {
    kind: 'garment', source: 'trust_legacy',
    colourId, garmentId,
    templateId: garmentId === 'healthcare_tunic' ? 'healthcare-tunic-v1' : 'scrub-vneck-v1',
    body: colour.body, trim: colour.trim, showBadge: true,
  };
}
